// Dev-only mock providers (spec 5). A `?dev=` query switch injects any session
// or authorization state so every screen in the spec is reachable in a local
// browser with no live issuer and no provisioning.
//
// SECURITY: every switch here is gated on `import.meta.env.DEV`, which Vite
// hard-codes to false in a production build. A production bundle therefore
// ignores these switches entirely and always resolves the real session (which
// is not_configured until a key is provisioned). This is why the dev harness is
// not a way around "build against not_configured": it does not exist in prod.

import type { SessionState } from './session/session'
import type { PartnerAuthorization } from './authz/partner'

const MOCK_PRINCIPAL = { id: 'dev-principal' } as const
const MOCK_ORG = { id: 'dev-org', name: 'Dev Org', roles: ['attester'] } as const

function params(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams()
  return new URLSearchParams(window.location.search)
}

/** A forced session state from `?dev-session=`, or null when none applies. */
export function mockSession(search: URLSearchParams = params()): SessionState | null {
  if (!import.meta.env.DEV) return null
  switch (search.get('dev-session')) {
    case 'verified': return { status: 'verified', principal: MOCK_PRINCIPAL }
    case 'expired': return { status: 'signed_out', cause: 'expired' }
    case 'invalid': return { status: 'signed_out', cause: 'invalid' }
    case 'none': return { status: 'signed_out', cause: 'none' }
    case 'not_configured': return { status: 'not_configured' }
    default: return null
  }
}

/** A forced authorization state from `?dev-authz=`, or null when none applies. */
export function mockAuthorization(
  search: URLSearchParams = params(),
): PartnerAuthorization | null {
  if (!import.meta.env.DEV) return null
  switch (search.get('dev-authz')) {
    case 'authorized': return { status: 'authorized', org: MOCK_ORG }
    case 'pending': return { status: 'pending' }
    case 'none': return { status: 'none' }
    case 'error': return { status: 'error' }
    default: return null
  }
}
